<template>
  <div class="pm-card scope-list" :qid="'scope-list-' + kind">
    <div class="pm-card__head scope-list__head">
      <span class="pm-icon pm-c-teal"><font-awesome-icon :icon="config.icon"/></span>
      <div>
        <h3>{{ $t(config.label) }}</h3>
        <div class="pm-card__sub">{{ countText }}</div>
      </div>
      <span class="grow"></span>
      <div class="pm-seg pm-seg--sm" v-if="canChooseAll">
        <div :class="{'on': !allMode}" @click="setAll(false)" qid="scope-mode-selected">{{ $t('projects.scope_tab.mode_selected') }}</div>
        <div :class="{'on': allMode}" @click="setAll(true)" qid="scope-mode-all">{{ $t('projects.scope_tab.mode_all') }}</div>
      </div>
      <div class="pm-search scope-list__search" v-if="!allMode && items.length > 8">
        <font-awesome-icon icon="search"/>
        <input type="text"
               v-model="filter"
               :placeholder="$t('projects.scope_tab.search')"
               qid="scope-search">
      </div>
      <b-button variant="success"
                size="sm"
                v-if="canEdit && !allMode"
                @click="$refs.picker.show()"
                qid="scope-add">
        <font-awesome-icon icon="list-ul" class="mr-1"/>{{ $t('projects.scope_tab.choose') }}
      </b-button>
    </div>

    <!-- All assets: no list, every round collects what exists when it starts -->
    <div class="scope-list__all" v-if="allMode" qid="scope-all-notice">
      <span class="pm-icon pm-c-teal"><font-awesome-icon icon="layer-group"/></span>
      <div>
        <b>{{ allTitle }}</b>
        <span>{{ $t('projects.scope_tab.all_notice') }}</span>
      </div>
    </div>

    <table class="pm-table" v-else>
      <thead>
        <tr>
          <th style="width:36%">{{ $t('projects.scope_tab.columns.name') }}</th>
          <th v-if="showOwners">{{ $t('projects.scope_tab.columns.owner') }}</th>
          <th v-if="showMembers">{{ $t('projects.scope_tab.columns.members') }}</th>
          <th v-if="showAssignees">{{ $t('projects.scope_tab.columns.also_assigned') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading"><td :colspan="columns" class="text-muted"><font-awesome-icon icon="spinner" class="fa-spin mr-2"/>{{ $t('projects.management.loading') }}</td></tr>
        <tr v-if="!loading && !items.length"><td :colspan="columns" class="text-muted">{{ mode === 'relations' ? $t('projects.scope_tab.relations_empty') : $t('projects.scope_tab.empty') }}</td></tr>
        <tr v-if="!loading && items.length && !shown.length"><td :colspan="columns" class="text-muted">{{ $t('projects.management.no_projects') }}</td></tr>
        <tr v-for="item in shown"
            :key="item.id"
            :class="{'scope-list__row--warn': noAssignee(item), 'scope-list__row--deleted': !item.model}"
            qid="scope-row">
          <td>
            <div class="pm-name" v-if="item.model">
              <span class="scope-list__dot"
                    v-if="noAssignee(item)"
                    v-b-tooltip.hover
                    :title="$t('projects.no_responsibles_tooltip_error')"></span>
              <div class="grow">
                <router-link :to="{name: config.route, params: {slug: item.model.slug}}" class="pm-name__title">{{ item.model.name }}</router-link>
                <div class="pm-name__ctx" v-if="item.model.tags_translated && item.model.tags_translated.length">
                  <span class="scope-list__tag" v-for="tag in item.model.tags_translated.slice(0, 3)" :key="tag.id || tag.name_translated">{{ tag.name_translated }}</span>
                </div>
              </div>
            </div>
            <div class="pm-name" v-else>
              <span class="pm-icon pm-icon--sm pm-c-grey"><font-awesome-icon icon="trash-alt"/></span>
              <div>
                <div class="pm-name__title text-muted">{{ $t('projects.scope_tab.deleted_item') }}</div>
                <div class="pm-name__ctx">{{ $t('projects.scope_tab.deleted_help') }}</div>
              </div>
            </div>
          </td>
          <td v-if="showOwners"><Team :users="item.model ? owners(item) : []"/></td>
          <td v-if="showMembers"><Team :users="item.model ? members(item) : []"/></td>
          <td v-if="showAssignees">
            <span class="scope-list__assignees">
              <Team :users="item.users"/>
              <span class="pm-row-action pm-row-action--neutral"
                    v-if="canEdit && item.model"
                    @click="editAssignees(item)"
                    v-b-tooltip.hover
                    :title="$t('projects.scope_tab.edit_assignees')"
                    qid="scope-edit-assignees">
                <font-awesome-icon icon="pen"/>
              </span>
            </span>
          </td>
          <td class="text-right">
            <span class="pm-row-action"
                  v-if="canEdit && (!allMode || !item.model)"
                  @click="confirmRemove(item)"
                  v-b-tooltip.hover
                  :title="$t('projects.scope_tab.remove_title')"
                  qid="scope-remove">
              <font-awesome-icon icon="times"/>
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <AssetPicker ref="picker"
                 :project="project"
                 :kind="kind"
                 :scopeItems="items"
                 @changed="changed"></AssetPicker>

    <!-- Extra people who get the tasks for one item -->
    <b-modal ref="assignees"
             hide-header
             hide-footer
             centered
             modal-class="project-launcher-modal">
      <div class="project-launcher" v-if="editing">
        <div class="project-launcher__head">
          <div class="grow">
            <h2>{{ $t('projects.scope_tab.edit_assignees') }}</h2>
            <div class="project-launcher__sub">{{ editing.model.name }}</div>
          </div>
          <span class="project-launcher__close" @click="$refs.assignees.hide()"><font-awesome-icon icon="times"/></span>
        </div>
        <div class="project-launcher__body">
          <div class="project-launcher__help mt-0">{{ $t('projects.scope_tab.assignees_help') }}</div>
          <multiselect
              v-model="editingUsers"
              :options="users"
              :multiple="true"
              track-by="id"
              label="name"
              :placeholder="$t('projects.scope_tab.edit_assignees')"
              :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')"
              @search-change="loadUsers"
              qid="scope-assignees-select"
          ></multiselect>
        </div>
        <div class="project-launcher__foot">
          <span class="grow"></span>
          <b-button variant="secondary" @click="$refs.assignees.hide()">{{ $t('projects.create.cancel') }}</b-button>
          <b-button variant="success"
                    class="ml-3"
                    :disabled="saving"
                    @click="saveAssignees"
                    qid="scope-assignees-save">{{ $t('tasks.modal.save') }}</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal ref="remove"
             hide-header
             hide-footer
             centered
             modal-class="project-launcher-modal">
      <div class="project-launcher" v-if="removing">
        <div class="project-launcher__head">
          <div class="grow">
            <h2>{{ $t('projects.scope_tab.remove_title') }}</h2>
            <div class="project-launcher__sub">{{ removing.model ? removing.model.name : $t('projects.scope_tab.deleted_item') }}</div>
          </div>
          <span class="project-launcher__close" @click="$refs.remove.hide()"><font-awesome-icon icon="times"/></span>
        </div>
        <div class="project-launcher__body">{{ $t('projects.scope_tab.remove_text') }}</div>
        <div class="project-launcher__foot">
          <span class="grow"></span>
          <b-button variant="secondary" @click="$refs.remove.hide()">{{ $t('projects.create.cancel') }}</b-button>
          <b-button variant="danger"
                    class="ml-3"
                    :disabled="saving"
                    @click="remove"
                    qid="scope-remove-confirm">{{ $t('projects.scope_tab.remove_title') }}</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import { scopeKind, kindParams, assetUsers } from '@/views/Projects/Scope/scopeKinds.js'
  import AssetPicker from '@/views/Projects/Scope/AssetPicker.vue';
  import Team from '@/views/Projects/Management/Team.vue';

  // The items in a project's scope, the same for every asset type and for the Relations tab (US 9396)
  export default {
    name: 'ProjectScope',
    components: {
      AssetPicker,
      Team
    },
    props: {
      project: {type: Object, required: true},
      kind: {type: String, required: true},
      // 'relations' lists what an engagement relates to; there's no "all" option and nobody gets tasks
      mode: {type: String, default: 'scope'},
      canEdit: {type: Boolean, default: false}
    },
    data: function() {
      return {
        loading: true,
        saving: false,
        items: [],
        liveTotal: null,
        filter: '',
        users: [],
        editing: null,
        editingUsers: [],
        removing: null
      }
    },
    computed: {
      config() {
        return scopeKind(this.kind);
      },
      getClient() {
        return this.$store.state.client;
      },
      isAudit() {
        return this.project.type === 'control_assessment';
      },
      // Control projects choose their own assignees for controls
      controlSettings() {
        return this.kind === 'control' && this.project.type === 'control';
      },
      showOwners() {
        return !this.isAudit && !!(this.controlSettings ? this.project.control_scope_owners : this.project.scope_owners);
      },
      showMembers() {
        return !this.isAudit && !!(this.controlSettings ? this.project.control_scope_members : this.project.scope_members);
      },
      showAssignees() {
        return !this.isAudit;
      },
      columns() {
        return 2 + (this.showOwners ? 1 : 0) + (this.showMembers ? 1 : 0) + (this.showAssignees ? 1 : 0);
      },
      canChooseAll() {
        return this.canEdit && this.mode === 'scope' && this.kind !== 'control' && this.kind === this.project.scope && !this.isAudit &&
          this.project.control_type === 'continuous';
      },
      allMode() {
        return this.mode === 'scope' && this.kind === this.project.scope && !!this.project.scope_all && this.project.control_type === 'continuous';
      },
      shown() {
        let filter = this.filter.trim().toLowerCase();
        if (!filter) {
          return this.items;
        }
        return _.filter(this.items, function(item) {
          return item.model && item.model.name && item.model.name.toLowerCase().indexOf(filter) !== -1;
        });
      },
      countText() {
        if (this.allMode) {
          return this.$t('projects.scope_tab.mode_all');
        }
        let key = this.mode === 'relations' ? 'projects.scope_tab.relations_count' : 'projects.scope_tab.count';
        return this.$tc(key, this.items.length, {count: this.items.length});
      },
      allTitle() {
        let label = this.$t(this.config.label);
        if (this.liveTotal === null) {
          return this.$t('projects.scope_tab.all_title', {items: label});
        }
        return this.$tc('projects.scope_tab.all_title_count', this.liveTotal, {count: this.liveTotal, items: label});
      }
    },
    methods: {
      owners(item) {
        return assetUsers(item.model.users, true);
      },
      members(item) {
        return assetUsers(item.model.users, false);
      },
      // Nobody would get the task for this item
      noAssignee(item) {
        if (!item.model || !this.showAssignees || this.mode !== 'scope') {
          return false;
        }
        return !(this.showOwners && this.owners(item).length) && !(this.showMembers && this.members(item).length) && !(item.users && item.users.length);
      },
      load() {
        this.loading = true;
        piincHttp.get('projects/' + this.project.slug + '/models', {params: {model_type: this.config.modelType, per_page: 2000, client: this.getClient.slug}}).then((response) => {
          this.items = _.sortBy(response.data.items, (item) => item.model ? item.model.name.toLowerCase() : '');
          this.loading = false;
        }, () => {
          this.loading = false;
        });
        if (this.allMode) {
          let params = Object.assign({client: this.getClient.slug, per_page: 1}, kindParams(this.kind, this.project));
          piincHttp.get(this.config.endpoint, {params: params}).then((response) => {
            this.liveTotal = response.data.total_count;
          }, () => {});
        }
      },
      changed() {
        this.load();
        this.$emit('updated');
      },
      setAll(value) {
        if (value === this.allMode || this.saving) {
          return;
        }
        this.saving = true;
        piincHttp.put('projects/' + this.project.slug + '/scope-all', {scope_all: value ? 1 : 0}).then((response) => {
          this.saving = false;
          this.$toastr('success', this.$t('projects.update.success'));
          this.$emit('updated', response.data);
        }, (error) => {
          this.saving = false;
          this.$toastr('error', error.data && error.data.message ? error.data.message : this.$t('projects.launcher.error_generic'));
        });
      },
      loadUsers(query) {
        let roles = [this.permissions.ROLE_CLIENT_ADMIN, this.permissions.ROLE_PARTITION_ADMIN];
        if (this.project.type === 'validation') {
          roles.push(this.permissions.ROLE_VALIDATOR);
        }
        if (['control', 'security_measure', 'risk_assessment', 'bia_assessment'].indexOf(this.project.type) !== -1) {
          roles.push(this.permissions.ROLE_MEMBER, this.permissions.ROLE_VALIDATOR);
        }
        piincHttp.get('users', {params: {client: this.getClient.id, keyword: query || undefined, role_name: roles, per_page: 50}}).then((response) => {
          this.users = response.data.items;
        }, () => {});
      },
      editAssignees(item) {
        this.editing = item;
        this.editingUsers = (item.users || []).slice();
        this.loadUsers();
        this.$refs.assignees.show();
      },
      saveAssignees() {
        this.saving = true;
        piincHttp.put('projects/' + this.project.slug + '/models/' + this.editing.id, {users: _.map(this.editingUsers, 'id')}).then(() => {
          this.saving = false;
          this.$refs.assignees.hide();
          this.$toastr('success', this.$t('projects.update.success'));
          this.changed();
        }, (error) => {
          this.saving = false;
          this.$toastr('error', error.data && error.data.message ? error.data.message : this.$t('projects.launcher.error_generic'));
        });
      },
      confirmRemove(item) {
        this.removing = item;
        this.$refs.remove.show();
      },
      remove() {
        this.saving = true;
        piincHttp.delete('projects/' + this.project.slug + '/models/' + this.removing.id).then(() => {
          this.saving = false;
          this.$refs.remove.hide();
          this.$toastr('success', this.$t('projects.scope_tab.removed'));
          this.changed();
        }, () => {
          this.saving = false;
        });
      }
    },
    watch: {
      'project.scope_all': function() {
        this.load();
      }
    },
    created() {
      this.load();
    }
  }
</script>
